import {useQueryClient, useSuspenseQuery} from '@github-ui/react-query'
import {createPersister} from '@github-ui/react-query-persister'
import {Compile} from '@github-ui/react-query-persister/typebox/compile'
import {throttle} from '@github/mini-throttle'
import {useAnalytics} from '@github-ui/use-analytics'
import {useAlive} from '@github-ui/use-alive'
import {reactFetch} from '@github-ui/verified-fetch'
import {useCurrentRepository} from '@github-ui/current-repository'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {useMemo, useCallback, useEffect} from 'react'
import {CodespacesDataSchema, type CodespacesData} from './codespaces-types'
import {buildCodespacesPath} from '../build-codespaces-path'
import {isCachingEnabled} from './flags'

const CodespacesDataValidator = Compile(CodespacesDataSchema)

function normalizeCodespacesJsonPath(codespacesPath?: string): string | undefined {
  if (!codespacesPath) return undefined

  const url = new URL(codespacesPath, 'https://github.local')
  url.searchParams.delete('current_branch')

  return `${url.pathname}${url.search}`
}

async function fetchCodespacesData(codespacesPath?: string): Promise<CodespacesData | null> {
  if (!codespacesPath) return null

  const result = await reactFetch(codespacesPath, {
    headers: {Accept: 'application/json'},
  })
  if (result.status >= 400 && result.status <= 499) {
    return null
  } else if (!result.ok) {
    throw new Error(`HTTP ${result.status}`)
  }

  const data = (await result.json()) as CodespacesData
  return data
}

const useCodespacesDataQueryOptions = (codespacesPathProp?: string) => {
  const repo = useCurrentRepository()
  const refInfo = useRefInfo()
  const codespacesPath =
    normalizeCodespacesJsonPath(codespacesPathProp) ||
    (repo && refInfo ? normalizeCodespacesJsonPath(buildCodespacesPath(repo.id, refInfo.name)) : undefined)
  const {sendAnalyticsEvent} = useAnalytics()

  return useMemo(
    () => ({
      queryKey: ['CodespacesTab.json', codespacesPath],
      queryFn: () => fetchCodespacesData(codespacesPath),
      staleTime: 60_000,
      persister: createPersister({
        validator: CodespacesDataValidator,
        sendAnalyticsEvent,
        enabled: isCachingEnabled(),
      }),
    }),
    [codespacesPath, sendAnalyticsEvent],
  )
}

export function useCodespacesData(codespacesPath?: string) {
  const queryOptions = useCodespacesDataQueryOptions(codespacesPath)
  return useSuspenseQuery<CodespacesData | null>(queryOptions)
}

export function usePrefetchCodespacesData(codespacesPath?: string) {
  const queryClient = useQueryClient()
  const queryOptions = useCodespacesDataQueryOptions(codespacesPath)

  return useCallback(() => {
    const isEnabled = isCachingEnabled()
    if (!isEnabled) return
    return queryClient.prefetchQuery(queryOptions)
  }, [queryClient, queryOptions])
}

export function useInvalidateCodespacesData(codespacesPath?: string) {
  const queryClient = useQueryClient()
  const queryOptions = useCodespacesDataQueryOptions(codespacesPath)

  return useCallback(() => {
    return queryClient.invalidateQueries({queryKey: queryOptions.queryKey})
  }, [queryClient, queryOptions.queryKey])
}

const ALIVE_THROTTLE_MS = 2000

export function useCodespacesAlive(aliveChannel: string | null | undefined, codespacesPath?: string) {
  const invalidate = useInvalidateCodespacesData(codespacesPath)
  const throttledInvalidate = useMemo(() => throttle(invalidate, ALIVE_THROTTLE_MS), [invalidate])

  useEffect(() => {
    return () => throttledInvalidate.cancel()
  }, [throttledInvalidate])

  useAlive(aliveChannel, throttledInvalidate)
}
